import { useEffect, useRef, useState } from 'react';

import { styled } from '@/stitches.config';

const StyledCursorButton = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'fixed',
  size: 56,
  br: '50%',
  bc: '$gray1000',
  zIndex: 1000,
  pe: 'none',
  color: '$white',
  transform: 'scale(0)',
  transition: 'transform .3s ease-in-out',
  us: 'none',
  cursor: 'none',
  bs: '0px 8px 16px rgba(0, 0, 0, 0.12)',
});

interface IProps {
  children?: React.ReactNode;
}

export const HOVER_CLASS = 'hoverable';

export const CursorButton = (props: IProps) => {
  const CURSOR_REF = useRef(null);
  const [getPosition, setGetPosition] = useState({ x: 0, y: 0 });
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const onResize = () => {
      if (!window.matchMedia) return;
      if (window.matchMedia('(pointer:coarse)').matches) {
        setIsTouchDevice(true);
      } else {
        setIsTouchDevice(false);
      }
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [isTouchDevice]);

  useEffect(() => {
    if (isTouchDevice) return;

    const HOVER_ABLE = document.querySelectorAll(`.${HOVER_CLASS}`);

    const handleMouseMove = (event: { clientX: any; clientY: any }) => {
      setGetPosition({
        x: event.clientX,
        y: event.clientY,
      });
    };

    const onMouseHover = (e) => {
      const el = e.currentTarget;
      const DISABLED_CLASS = el.classList.contains('swiper-button-disabled');
      if (DISABLED_CLASS) return;

      const hoverText = el.getAttribute('data-hover-text');
      const hoverBg = el.getAttribute('data-hover-bg');
      const hoverIcon = el.querySelector('i');

      if (hoverIcon) {
        const cloneIcon = hoverIcon.cloneNode(true);

        CURSOR_REF.current.appendChild(cloneIcon);
      }

      if (hoverText) {
        CURSOR_REF.current.innerHTML = hoverText;
      }

      if (hoverBg) {
        CURSOR_REF.current.style.backgroundColor = hoverBg;
      }

      CURSOR_REF.current.style.transform = 'scale(1)';
      document.body.style.cursor = 'none';
    };

    const onMouseHoverOut = () => {
      CURSOR_REF.current.innerHTML = '';
      CURSOR_REF.current.style.backgroundColor = '';
      CURSOR_REF.current.style.transform = 'scale(0)';
      document.body.style.cursor = 'auto';
    };

    const onClick = (e) => {
      const el = e.currentTarget;
      const DISABLED_CLASS = el.classList.contains('swiper-button-disabled');

      if (!DISABLED_CLASS) return;

      CURSOR_REF.current.innerHTML = '';
      CURSOR_REF.current.style.backgroundColor = '';
      CURSOR_REF.current.style.transform = 'scale(0)';
      document.body.style.cursor = 'auto';
    };

    const handleMouseDown = (e) => {
      const el = e.currentTarget;
      const DISABLED_CLASS = el.classList.contains('swiper-button-disabled');

      if (DISABLED_CLASS) return;
      CURSOR_REF.current.style.transform = 'scale(0.6)';
    };

    const handleMouseUp = () => {
      CURSOR_REF.current.style.transform = 'scale(1)';
    };

    window.addEventListener('mousemove', handleMouseMove);

    HOVER_ABLE.forEach((target) => {
      target.addEventListener('mouseenter', onMouseHover);
      target.addEventListener('mouseleave', onMouseHoverOut);
      target.addEventListener('click', onClick);
      target.addEventListener('mousedown', handleMouseDown);
      target.addEventListener('mouseup', handleMouseUp);
    });

    return () => {
      if (CURSOR_REF.current) {
        CURSOR_REF.current.innerHTML = '';
        CURSOR_REF.current.style.backgroundColor = '';
        // eslint-disable-next-line react-hooks/exhaustive-deps
        CURSOR_REF.current.style.transform = 'scale(0)';
      }

      document.body.style.cursor = 'auto';
      window.removeEventListener('mousemove', handleMouseMove);

      HOVER_ABLE.forEach((target) => {
        target.removeEventListener('mouseenter', onMouseHover);
        target.removeEventListener('mouseleave', onMouseHoverOut);
        target.removeEventListener('click', onClick);
        target.removeEventListener('mousedown', handleMouseDown);
        target.removeEventListener('mouseup', handleMouseUp);
      });
    };
  }, [isTouchDevice]);

  return (
    <StyledCursorButton
      ref={CURSOR_REF}
      style={{
        left: getPosition.x - 28,
        top: getPosition.y - 28,
      }}
    >
      {props.children}
    </StyledCursorButton>
  );
};
CursorButton.displayName = 'SharedMY24CursorButton';
